import React from 'react';
import PropTypes from 'prop-types';
import {FlatButton, MenuItem, SelectField, TextField} from "material-ui";
import {Map, Set, List, fromJS} from 'immutable';
import IconButton from "../material/IconButton";
import OverlayController from "../overlay/OverlayController";
import Dialog from "../overlay/Dialog";
import { compose } from 'react-apollo';
import queries from '../../graphql/queries';
import {Operators, Types, FieldOperatorsByType, dateMathRegExp, MutuallyExclusiveOperatorBuckets, numberRegExp} from '../../services/queueQueryEnums';
import _ from 'lodash';


const OperatorText = {
    [Operators.gt]: 'is greater than',
    [Operators.gte]: 'is greater than or equal to',
    [Operators.lt]: 'is less than',
    [Operators.lte]: 'is less than or equal to',
    [Operators.eq]: 'is equal to',
    [Operators.ne]: 'is not equal to',
    [Operators.in]: 'has any value in',
    [Operators.nin]: 'has no value in',
    [Operators.all]: 'contains all',
    [Operators.exists]: 'exists'
};

const Fields = [
    {name: 'tags', types: [Types.keyword]},
    {name: 'createdAt', types: [Types.date]},
    {name: 'updatedAt', types: [Types.date]},
    {name: 'assignedUsers.userId', types: [Types.keyword]},
    {name: 'assignedUsers.flowId', types: [Types.keyword]}
];

const FieldsByName = _.keyBy(Fields, 'name');


export default compose(
    queries.itemDataFieldsQueryHoc,
)(class extends React.Component {

    static propTypes = {
        projectId: PropTypes.string.isRequired,
        query: PropTypes.instanceOf(Map),
        onQueryChange: PropTypes.func.isRequired,
        itemDataFieldsData: PropTypes.shape({
            error: PropTypes.object,
            loading: PropTypes.bool,
            itemDataFields: PropTypes.arrayOf(PropTypes.shape({
                key: PropTypes.string,
                types: PropTypes.arrayOf(PropTypes.string)
            }))
        })
    };

    static defaultProps = {

    };

    constructor(props) {
        super(props);

        this.fieldsByName = FieldsByName;
        this.updateForProps(props);

        this.state = {
            editFieldName: null,
            editFieldType: null,
            editOperator: null,
            filterFieldName: null,
            filterFieldType: null,
            filterOperator: null,
            filterValue: null
        };
    }

    componentWillReceiveProps(nextProps) {
        this.updateForProps(nextProps);
    }

    updateForProps(props) {
        if (!props.itemDataFieldsData.loading && !props.itemDataFieldsData.error) {
            let itemDataFieldsByNameAndType = _.transform(props.itemDataFieldsData.itemDataFields, (map, itemDataField) => {
                let fieldName = 'data.' + itemDataField.key;
                map[fieldName] = {
                    name: fieldName,
                    types: itemDataField.types.slice()
                };
            }, {});
            this.fieldsByName = Object.assign({}, FieldsByName, itemDataFieldsByNameAndType);
        }
    }

    onFilterFieldNameChange = (event, index, fieldName) => {
        let fieldType = null;
        if (this.fieldsByName[fieldName].types.length === 1) {
            fieldType = this.fieldsByName[fieldName].types[0];
        }
        this.setState({
            filterFieldName: fieldName,
            filterFieldType: fieldType,
            filterOperator: null,
            filterValue: null
        });
    };

    onFilterFieldTypeChange = (event, index, fieldType) => {
        this.setState({
            filterFieldType: fieldType,
            filterOperator: null,
            filterValue: null
        });
    };

    onFilterOperatorChange = (event, index, filterOperator) => {
        let filterValue = '';
        if (this.state.filterFieldType === Types.null || filterOperator === Operators.exists) {
            filterValue = false;
        }
        this.setState({
            filterOperator: filterOperator,
            filterValue: filterValue
        });
    };

    onFilterValueChange = (event) => {
        this.setState({
            filterValue: event.target.value
        });
    };

    onBooleanFilterValueChange = (event, index, value) => {
        this.setState({
            filterValue: value
        });
    };

    onAddFilterClick = () => {
        let query = this.props.query;
        if (!query) {
            query = fromJS({
                type: 'json',
                query: {}
            });
        }
        let filterValue = this.state.filterValue;
        if (typeof filterValue === "string") {
            filterValue = filterValue.trim();
        }
        if ([Operators.in, Operators.nin, Operators.all].includes(this.state.filterOperator)) {
            filterValue = fromJS(filterValue.split(' '));
        }
        if (Types.number === this.state.filterFieldType) {
            if ([Operators.in, Operators.nin, Operators.all].includes(this.state.filterOperator)) {
                filterValue = filterValue.map(Number);
            } else {
                filterValue = Number(filterValue);
            }
        }
        let newQuery = query.setIn(['query', this.state.filterFieldName, this.state.filterFieldType, this.state.filterOperator], filterValue);
        this.props.onQueryChange(newQuery);
        this.setState({
            filterFieldName: null,
            filterFieldType: null,
            filterOperator: null,
            filterValue: null
        });
    };

    onSaveFilterClick = () => {
        let newQuery = this.props.query.withMutations(query => {
            // If field name, field type or operator have been changed, delete the old rows.
            let fieldNameChanged = this.state.editFieldName !== this.state.filterFieldName;
            let fieldTypeChanged = this.state.editFieldType !== this.state.filterFieldType;
            let operatorChanged = this.state.editOperator !== this.state.filterOperator;
            if (fieldNameChanged || fieldTypeChanged || operatorChanged) {
                query.deleteIn(['query', this.state.editFieldName, this.state.editFieldType, this.state.editOperator]);
                if (fieldNameChanged || fieldTypeChanged) {
                    if (query.getIn(['query', this.state.editFieldName, this.state.editFieldType]).size === 0) {
                        query.deleteIn(['query', this.state.editFieldName, this.state.editFieldType]);
                        if (fieldNameChanged) {
                            if (query.getIn(['query', this.state.editFieldName]).size === 0) {
                                query.deleteIn(['query', this.state.editFieldName]);
                            }
                        }
                    }
                }
            }
            let filterValue = this.state.filterValue;
            if (typeof filterValue === "string") {
                filterValue = filterValue.trim();
            }
            if ([Operators.in, Operators.nin, Operators.all].includes(this.state.filterOperator)) {
                filterValue = fromJS(filterValue.split(' '));
            }
            if (Types.number === this.state.filterFieldType) {
                if ([Operators.in, Operators.nin, Operators.all].includes(this.state.filterOperator)) {
                    filterValue = filterValue.map(Number);
                } else {
                    filterValue = Number(filterValue);
                }
            }
            query.setIn(['query', this.state.filterFieldName, this.state.filterFieldType, this.state.filterOperator], filterValue);
        });
        this.props.onQueryChange(newQuery);
        this.setState({
            editFieldName: null,
            editFieldType: null,
            editOperator: null,
            filterFieldName: null,
            filterFieldType: null,
            filterOperator: null,
            filterValue: null
        });
    };

    onCancelAddFilterClick = () => {
        this.setState({
            editFieldName: null,
            editFieldType: null,
            editOperator: null,
            filterFieldName: null,
            filterFieldType: null,
            filterOperator: null,
            filterValue: null
        });
    };

    onEditFilterClick(fieldName, fieldType, operator, value) {
        if (List.isList(value)) {
            value = value.join(' ');
        }
        if (_.isNumber(value)) {
            value = String(value);
        }
        this.setState({
            editFieldName: fieldName,
            editFieldType: fieldType,
            editOperator: operator,
            filterFieldName: fieldName,
            filterFieldType: fieldType,
            filterOperator: operator,
            filterValue: value
        });
    }

    onDeleteFilterClick(fieldName, fieldType, operator) {
        let actions = [
            {label: 'Cancel', handler: () => OverlayController.hideOverlay()},
            {label: 'Delete', handler: () => this.onApproveDeleteClick(fieldName, fieldType, operator)}
        ];
        OverlayController.showOverlay({
            component: <Dialog title={`Are you sure?`} actions={actions} />
        });
    }

    onApproveDeleteClick(fieldName, fieldType, operator) {
        let newQuery = this.props.query.withMutations(query => {
            query.deleteIn(['query', fieldName, fieldType, operator]);
            if (query.getIn(['query', fieldName, fieldType]).size === 0) {
                query.deleteIn(['query', fieldName, fieldType]);
                if (query.getIn(['query', fieldName]).size === 0) {
                    query.deleteIn(['query', fieldName]);
                }
            }
        });
        if (newQuery.get('query').size === 0) {
            newQuery = null;
        }
        this.props.onQueryChange(newQuery);
        OverlayController.hideOverlay();
    }

    isValidFilterValue() {
        if (typeof this.state.filterValue === 'boolean') {
            return true;
        }
        let value = this.state.filterValue.trim();
        if (!value.length) {
            return false;
        }
        switch (this.state.filterFieldType) {
            case Types.date:
                return null !== value.match(dateMathRegExp);
            case Types.number:
                return _.chain(value.split(' ')).every(value => value.match(numberRegExp)).value();
            default:
                return true;
        }
    }

    renderFilterRows() {
        let query = this.props.query;
        if (!query) {
            return null;
        }
        let rows = [];
        query.get('query').forEach((fieldTypes, fieldName) => {
            fieldTypes.forEach((operators, fieldType) => {
                operators.forEach((fieldValue, operator) => {
                    if (this.state.editFieldName === fieldName && this.state.editFieldType === fieldType && this.state.editOperator === operator) {
                        rows.push(
                            <div key={`${fieldName}-${fieldType}-${operator}`} className="editFilterRow">
                                {this.renderFieldNameSelectField(false)}
                                {this.renderFieldTypeSelectField()}
                                {this.renderOperatorSelectField()}
                                {this.renderValueSelectField()}
                                {this.renderSaveButton()}
                                {this.renderCancelButton()}
                            </div>
                        );
                    } else {
                        let onEditFilterClick;
                        let onDeleteFilterClick;
                        let disabled = false;
                        if (this.state.filterFieldName) {
                            disabled = true;
                        } else {
                            onEditFilterClick = () => this.onEditFilterClick(fieldName, fieldType, operator, fieldValue);
                            onDeleteFilterClick = () => this.onDeleteFilterClick(fieldName, fieldType, operator);
                        }
                        let value = fieldValue;
                        if (fieldType === Types.boolean || operator === Operators.exists) {
                            value = String(fieldValue);
                        } else if (fieldType === Types.null) {
                            value = 'null';
                        } else if (List.isList(fieldValue)) {
                            value = fieldValue.join(', ');
                        }
                        rows.push(
                            <div key={`${fieldName}-${fieldType}-${operator}`} className="materialListItem singleLine">
                                <div className="materialListItemText">{fieldName} ({fieldType}) {OperatorText[operator]} {value}</div>
                                <div className="filterButtons">
                                    <IconButton icon={<i className="material-icons md-18">edit</i>} disabled={disabled} buttonSize={IconButton.ButtonSize.buttonSize40} iconFontSize={IconButton.IconFontSize.iconFontSize18} onClick={onEditFilterClick}/>
                                    <IconButton icon={<i className="material-icons md-18">delete</i>} disabled={disabled} buttonSize={IconButton.ButtonSize.buttonSize40} iconFontSize={IconButton.IconFontSize.iconFontSize18} onClick={onDeleteFilterClick}/>
                                </div>
                            </div>
                        );
                    }
                });
            });
        });
        return <div className="materialList dense filterRows">{rows}</div>;
    }

    renderFieldNameSelectField(disabled) {
        let menuItems = _.map(this.fieldsByName, (field, fieldName) => {
            // Check if all types and their allowed operators for this field name already used in the given query.
            // Disable this MenuItem if this is the case. If this field name is currently being edited, then ignore and
            // do not disable this MenuItem so the original field name could be selected back if it was changed.
            let allTypesUsed = false;
            if (this.props.query && fieldName !== this.state.editFieldName) {
                let usedTypes = this.props.query.getIn(['query', fieldName], null);
                if (usedTypes && _.difference(field.types, usedTypes.toJS()).length === 0) {
                    allTypesUsed = (/** @type Immutable.Iterable */usedTypes.keySeq()).every(type => {
                        let usedOperatorsSet = new Set();
                        let allOperatorsUsed = false;
                        let usedOperators = this.props.query.getIn(['query', fieldName, type], null);
                        if (usedOperators) {
                            (/** @type Immutable.Iterable */usedOperators.keySeq()).forEach(operator => {
                                usedOperatorsSet = usedOperatorsSet.add(operator);
                                usedOperatorsSet = usedOperatorsSet.union(MutuallyExclusiveOperatorBuckets[operator])
                            });
                            allOperatorsUsed = FieldOperatorsByType[type].every(operator => {
                                return usedOperatorsSet.has(operator);
                            });
                        }
                        return allOperatorsUsed;
                    });
                }
            }
            return <MenuItem key={fieldName} value={fieldName} primaryText={fieldName} disabled={allTypesUsed}/>;
        });
        let value = disabled ? null : this.state.filterFieldName;
        return (
            <SelectField
                className="custom-field-no-label custom-select-field filterFieldNameSelect"
                hintText="Choose field to filter by"
                value={value}
                autoWidth={true}
                onChange={this.onFilterFieldNameChange}
                disabled={disabled}>
                {menuItems}
            </SelectField>
        );
    }

    renderFieldTypeSelectField() {
        if (!this.state.filterFieldName) {
            return null;
        }
        let disabled = this.fieldsByName[this.state.filterFieldName].types.length === 1;
        let menuItems = this.fieldsByName[this.state.filterFieldName].types.map(type => {
            // Check if all operators for this field name and type already used in the given query. Disable this
            // MenuItem if this is the case. If this field name and type are currently being edited, then ignore and do
            // not disable this MenuItem so the original type could be selected back if it was changed.
            let allOperatorsUsed = false;
            if (this.props.query && !(this.state.filterFieldName === this.state.editFieldName && type === this.state.editFieldType)) {
                let usedOperatorsSet = new Set();
                let usedOperators = this.props.query.getIn(['query', this.state.filterFieldName, type], null);
                if (usedOperators) {
                    (/** @type Immutable.Iterable */usedOperators.keySeq()).forEach(operator => {
                        usedOperatorsSet = usedOperatorsSet.add(operator);
                        usedOperatorsSet = usedOperatorsSet.union(MutuallyExclusiveOperatorBuckets[operator])
                    });
                    allOperatorsUsed = FieldOperatorsByType[type].every(operator => {
                        return usedOperatorsSet.has(operator);
                    });
                }
            }
            return <MenuItem key={type} value={type} primaryText={type} disabled={allOperatorsUsed}/>;
        });
        return (
            <SelectField
                className="custom-field-no-label custom-select-field filterFieldTypeSelect"
                hintText="type"
                value={this.state.filterFieldType}
                autoWidth={true}
                onChange={this.onFilterFieldTypeChange}
                disabled={disabled}>
                {menuItems}
            </SelectField>
        );
    }

    renderOperatorSelectField() {
        if (!this.state.filterFieldType) {
            return null;
        }
        // Create a set of operators that have been already used in a given query for the selected field name and type.
        // Ignore the currently being edited operator, so the original operator could be selected back if it was changed.
        // Then disable MenuItems for operators that have been already used.
        let usedOperatorsSet = new Set();
        if (this.props.query) {
            let usedOperators = this.props.query.getIn(['query', this.state.filterFieldName, this.state.filterFieldType], null);
            if (usedOperators) {
                let editingSameFieldAndType = this.state.filterFieldName === this.state.editFieldName && this.state.filterFieldType === this.state.editFieldType;
                (/** @type Immutable.Iterable */usedOperators.keySeq()).forEach(operator => {
                    if (!(editingSameFieldAndType && operator === this.state.editOperator)) {
                        usedOperatorsSet = usedOperatorsSet.add(operator);
                        usedOperatorsSet = usedOperatorsSet.union(MutuallyExclusiveOperatorBuckets[operator])
                    }
                });
            }
        }
        let menuItems = FieldOperatorsByType[this.state.filterFieldType].map(operator => {
            let alreadyDefined = usedOperatorsSet.has(operator);
            return <MenuItem key={operator} value={operator} primaryText={OperatorText[operator]} disabled={alreadyDefined}/>;
        });
        return (
            <SelectField
                className="custom-field-no-label custom-select-field filterOperatorSelect"
                hintText="comparison operator"
                value={this.state.filterOperator}
                onChange={this.onFilterOperatorChange}>
                {menuItems}
            </SelectField>
        );
    }

    renderValueSelectField() {
        if (!this.state.filterOperator) {
            return null;
        }
        if (this.state.filterFieldType === Types.boolean || this.state.filterOperator === Operators.exists) {
            return (
                <SelectField
                    className="custom-field-no-label custom-select-field filterOperatorSelect"
                    hintText="true or false"
                    value={this.state.filterValue}
                    onChange={this.onBooleanFilterValueChange}>
                    <MenuItem value={true} primaryText={'true'}/>
                    <MenuItem value={false} primaryText={'false'}/>
                </SelectField>
            );
        }
        let value = this.state.filterValue;
        let disabled = false;
        if (this.state.filterFieldType === Types.null) {
            value = 'null';
            disabled = true;
        }
        let hintText = 'value';
        if ([Operators.in, Operators.nin, Operators.all].includes(this.state.filterOperator)) {
            hintText = 'space separated values'
        }
        return (
            <TextField
                className="custom-field-no-label filterValueText"
                hintText={hintText}
                value={value}
                disabled={disabled}
                onChange={this.onFilterValueChange}
            />
        );
    }

    renderAddButton() {
        if (!this.state.filterOperator) {
            return null;
        }
        return <FlatButton
            label="Add"
            primary={true}
            disabled={!this.isValidFilterValue()}
            onClick={this.onAddFilterClick}
        />;
    }

    renderSaveButton() {
        if (!this.state.filterOperator) {
            return null;
        }
        return <FlatButton
            label="Save"
            primary={true}
            disabled={!this.isValidFilterValue()}
            onClick={this.onSaveFilterClick}
        />;
    }

    renderCancelButton() {
        if (!this.state.filterFieldName) {
            return null;
        }
        return <FlatButton
            className="addFilterCancelButton"
            label="Cancel"
            primary={true}
            onClick={this.onCancelAddFilterClick}
        />;
    }

    render() {
        let editing = this.state.editFieldName !== null;
        return (
            <div>
                <div className="statesLabel">Filters</div>
                {this.renderFilterRows()}
                <div className="addFilterRow">
                    {this.renderFieldNameSelectField(editing)}
                    {!editing && this.renderFieldTypeSelectField()}
                    {!editing && this.renderOperatorSelectField()}
                    {!editing && this.renderValueSelectField()}
                    {!editing && this.renderAddButton()}
                    {!editing && this.renderCancelButton()}
                </div>
            </div>
        );
    }
});



// WEBPACK FOOTER //
// ./src/components/queue/QueueFilterSection.js